import React, { useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { AgTechTourStep } from './AgTechTourTypes'
import { useAgTechTour } from './AgTechTourContext'
import { useAgTechTourTargetRect } from './useAgTechTourTargetRect'

type CalloutProps = {
    step: AgTechTourStep
    stepIndex: number
    totalSteps: number
}

const PADDING = 6
const CALLOUT_GAP = 16

const AgTechTourCallout = ({ step, stepIndex, totalSteps }: CalloutProps) => {
    const { nextStep, prevStep, closeTour } = useAgTechTour()
    const calloutRef = useRef<HTMLDivElement>(null)
    const [pos, setPos] = useState<{ top: number; left: number }>({ top: -9999, left: -9999 })

    const rect = useAgTechTourTargetRect(step.targetId)

    useLayoutEffect(() => {
        const callout = calloutRef.current
        if (!callout) return

        const vw = window.innerWidth
        const vh = window.innerHeight
        const calloutW = callout.offsetWidth
        const calloutH = callout.offsetHeight

        let top: number
        let left: number

        if (rect) {
            const r = rect
            const highlightBottom = r.top + r.height + PADDING
            const highlightTop = r.top - PADDING
            const highlightCenterX = r.left + r.width / 2

            // default: below
            if (highlightBottom + calloutH + CALLOUT_GAP < vh) {
                top = highlightBottom + CALLOUT_GAP
            } else {
                top = highlightTop - calloutH - CALLOUT_GAP
            }

            left = highlightCenterX - calloutW / 2
        } else {
            top = vh / 2 - calloutH / 2
            left = vw / 2 - calloutW / 2
        }

        // clamp to viewport
        left = Math.max(12, Math.min(left, vw - calloutW - 12))
        top = Math.max(12, Math.min(top, vh - calloutH - 12))

        setPos({ top, left })
    }, [step.targetId, step.title, rect?.top, rect?.left, rect?.width, rect?.height])

    const isFirst = stepIndex === 0
    const isLast = stepIndex === totalSteps - 1

    const surface = document.getElementById('agtech-app') ?? document.body

    const callout = (
        <div
            ref={calloutRef}
            className="agtech-tour-ui fixed bg-white rounded border border-slate-200 shadow-lg p-5 w-[320px]"
            style={{ top: pos.top, left: pos.left, zIndex: 9999 }}
        >
            <div className="text-xs text-slate-400 mb-2 font-medium">
                Step {stepIndex + 1} of {totalSteps}
            </div>
            <h3 className="text-sm font-semibold text-slate-800 mb-2">{step.title}</h3>
            <p className="text-sm text-slate-600 mb-4 leading-relaxed">{step.description}</p>
            <div className="flex items-center gap-x-2">
                <button
                    className="text-xs text-slate-400 hover:text-slate-600 mr-auto"
                    onClick={closeTour}
                >
                    Skip Tour
                </button>
                <button
                    className={`px-3 py-1.5 text-xs rounded border border-slate-200 font-medium transition-colors ${isFirst ? 'text-slate-300 cursor-not-allowed' : 'text-slate-600 hover:bg-slate-50'}`}
                    onClick={prevStep}
                    disabled={isFirst}
                >
                    Back
                </button>
                <button
                    className="px-3 py-1.5 text-xs rounded font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    onClick={nextStep}
                >
                    {isLast ? 'Finish' : 'Next →'}
                </button>
            </div>
        </div>
    )

    return ReactDOM.createPortal(callout, surface)
}

export default AgTechTourCallout
